import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Package, Truck, ShoppingCart, Wrench, X, Inbox, BookOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  'SKU':            { icon: Package,      color: 'text-purple-500' },
  'Asset':          { icon: Truck,        color: 'text-indigo-500' },
  'Purchase Order': { icon: ShoppingCart, color: 'text-amber-500' },
  'Maintenance':    { icon: Wrench,       color: 'text-rose-500' },
  'Parts Ticket':   { icon: Inbox,        color: 'text-blue-500' },
  'Part Catalog':   { icon: BookOpen,     color: 'text-emerald-500' },
};

const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Ctrl+K / Cmd+K to open
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQuery(''); setResults([]); setSelected(0); }
  }, [open]);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/search?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(res.data || []);
      setSelected(0);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(query), 300);
    return () => clearTimeout(t);
  }, [query, search]);

  const go = (url) => {
    navigate(url);
    setOpen(false);
  };

  const handleKey = (e) => {
    if (e.key === 'ArrowDown') setSelected(s => Math.min(s + 1, results.length - 1));
    if (e.key === 'ArrowUp') setSelected(s => Math.max(s - 1, 0));
    if (e.key === 'Enter' && results[selected]) go(results[selected].url);
  };

  if (!open) return (
    <button
      onClick={() => setOpen(true)}
      data-testid="global-search-btn"
      className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-sm text-slate-500 bg-slate-100 border border-slate-200 rounded-lg hover:bg-slate-200 transition-colors"
    >
      <Search className="h-4 w-4" />
      <span>Search</span>
      <kbd className="ml-2 text-xs bg-white border border-slate-300 rounded px-1.5 py-0.5 font-mono">⌘K</kbd>
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4" data-testid="global-search-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />

      {/* Modal */}
      <div className="relative w-full max-w-xl bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
          <Search className="h-5 w-5 text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Search SKUs, assets, POs, maintenance, tickets, catalog..."
            className="flex-1 text-sm outline-none text-slate-900 placeholder:text-slate-400"
            data-testid="global-search-input"
          />
          {loading && <div className="h-4 w-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />}
          <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <ul className="max-h-72 overflow-y-auto py-2">
            {results.map((r, i) => {
              const cfg = TYPE_CONFIG[r.type] || { icon: Search, color: 'text-slate-400' };
              const Icon = cfg.icon;
              return (
                <li
                  key={r.id}
                  onClick={() => go(r.url)}
                  className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${i === selected ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
                  data-testid={`search-result-${i}`}
                >
                  <Icon className={`h-4 w-4 flex-shrink-0 ${cfg.color}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{r.title}</p>
                    <p className="text-xs text-slate-500 truncate">{r.subtitle}</p>
                  </div>
                  <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full flex-shrink-0">{r.type}</span>
                </li>
              );
            })}
          </ul>
        )}

        {query.length >= 2 && !loading && results.length === 0 && (
          <p className="px-4 py-6 text-sm text-center text-slate-500">No results for "{query}"</p>
        )}

        {query.length < 2 && (
          <p className="px-4 py-4 text-xs text-slate-400 text-center">Type at least 2 characters to search</p>
        )}
      </div>
    </div>
  );
};

export default GlobalSearch;
